/**
 * Multimodal Preview Component
 *
 * Main component for rendering previews of multimodal content (images, audio, PDFs).
 * Delegates to specialized preview components for each content type.
 *
 * @module features/chat/components/MultimodalPreview
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { File, X as XIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  isStorageImageBlock,
  isStoragePdfBlock,
} from "@/lib/storage";
import type { ContentBlock } from "../types/messages";

// Import specialized preview components
import { AudioPreview } from "./preview/AudioPreview";
import { ImagePreview } from "./preview/ImagePreview";
import { PdfPreview } from "./preview/PdfPreview";

// Re-export AudioPreview for backward compatibility
export { AudioPreview } from "./preview/AudioPreview";

// =============================================================================
// TYPES
// =============================================================================

export interface MultimodalPreviewProps {
  block: ContentBlock;
  removable?: boolean;
  onRemove?: () => void;
  onImageClick?: () => void;
  className?: string;
  size?: "sm" | "md" | "lg";
  label?: number;
  variant?: "compact" | "full";
  transcript?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const MultimodalPreview: React.FC<MultimodalPreviewProps> = ({
  block,
  removable = false,
  onRemove,
  onImageClick,
  className,
  size = "md",
  label,
  variant = "compact",
  transcript,
}) => {
  const { t } = useTranslation("chat");

  // Image block (storage-referenced or base64)
  if (
    isStorageImageBlock(block) ||
    (block.type === "image" &&
      block.source_type === "base64" &&
      typeof block.mime_type === "string" &&
      block.mime_type.startsWith("image/"))
  ) {
    return (
      <ImagePreview
        block={block as any}
        removable={removable}
        onRemove={onRemove}
        onImageClick={onImageClick}
        className={className}
        size={size}
      />
    );
  }

  // PDF block (storage-referenced or base64)
  if (
    isStoragePdfBlock(block) ||
    (block.type === "file" &&
      block.source_type === "base64" &&
      block.mime_type === "application/pdf")
  ) {
    return (
      <PdfPreview
        block={block as any}
        removable={removable}
        onRemove={onRemove}
        className={className}
        size={size}
      />
    );
  }

  // Audio block (storage-referenced or base64)
  if (
    block.type === "audio" &&
    (block.source_type === "storage" || block.source_type === "base64") &&
    typeof block.mime_type === "string" &&
    block.mime_type.startsWith("audio/")
  ) {
    return (
      <AudioPreview
        block={block}
        removable={removable}
        onRemove={onRemove}
        className={className}
        size={size}
        label={label}
        variant={variant}
        transcript={transcript}
      />
    );
  }

  // Fallback for unknown types
  return (
    <div
      className={cn(
        "bg-muted text-muted-foreground flex items-center gap-2 border px-3 py-2",
        className
      )}
    >
      <File className="h-5 w-5 flex-shrink-0" />
      <span className="truncate text-sm">
        {t("multimodal.unsupportedFileType")}
      </span>
      {removable && (
        <button
          type="button"
          className="bg-background text-muted-foreground hover:bg-muted ml-2 rounded-full p-1 cursor-pointer"
          onClick={onRemove}
          aria-label={t("multimodal.removeFile")}
        >
          <XIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
